import React from 'react';
import BeaconChartWithLegend, {
  BeaconChartWithLegendProps
} from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import {
  SplineChartV2,
  SplineChartV2Props,
  useMetricByWeekIdV2,
  UseMetricByWeekIdV2Args,
  UseMetricByWeekIdV2Result
} from 'src/components/BeaconV2Components/SplineChartV2';
import { useAppSelector } from 'src/utils/Hooks';
import SplineChartLoading, {
  SplineChartLoadingProps
} from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { getAppName } from 'src/utils/app';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import convertSplineChartV2SeriesToDelimitedData from 'src/components/Charts/GenericChart/SeriesConverters/splineChartV2';
import saveAs from 'file-saver';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';

export interface AtlasV2MetricSplineChartProps
  extends Pick<
      BeaconChartWithLegendProps,
      'singleDropdownProps' | 'buildDropdownMenuOptions' | 'dropdownOptionHandler'
    >,
    UseMetricByWeekIdV2Args {
  title?: string;
  /**
   * Hides the title and category subtitle if false
   */
  showTitleSummary?: boolean;
  /**
   * Used for indexing overrides for the index field
   */
  entityType?: string;
  splineChartLoadingProps?: SplineChartLoadingProps;
  beaconChartWithLegendOverride?: (
    props: Omit<BeaconChartWithLegendProps, 'children'>,
    metricByWeekIdValues: UseMetricByWeekIdV2Result
  ) => Omit<BeaconChartWithLegendProps, 'children'>;
  splineChartProps?: Partial<SplineChartV2Props>;
}

/**
 * Given an index and a field name, displays a weekly spline chart using SplineChartV2.
 * Uses the new useMetricByWeekIdV2 hook for data fetching.
 */
const AtlasV2MetricSpineChart = ({
  fieldName,
  indexName,
  title,
  showTitleSummary = true,
  entityType,
  primaryAggregationBuilder,
  secondaryAggregationBuilder,
  splineChartLoadingProps,
  metricTypeOverride,
  requestBuilder,
  comparisonFieldName,
  comparisonIndexName,
  buildDropdownMenuOptions,
  dropdownOptionHandler,
  beaconChartWithLegendOverride,
  splineChartProps,
  useGenericAdvancedSearchArgs,
  ...chartWithLegendProps
}: AtlasV2MetricSplineChartProps) => {
  const metricByWeekIdValues = useMetricByWeekIdV2({
    fieldName,
    indexName,
    entityType,
    primaryAggregationBuilder,
    secondaryAggregationBuilder,
    metricTypeOverride,
    requestBuilder,
    comparisonFieldName,
    comparisonIndexName,
    useGenericAdvancedSearchArgs
  });

  const {
    primarySeries,
    comparisonSeries,
    isLoading,
    formattedPrimaryTotal,
    formattedComparisonTotal,
    formattedPercentChange,
    percentChange,
    formattedLastPrimaryValue,
    formattedLastComparisonValue,
    lastValuePercentChange,
    formattedLastValuePercentChange,
    metricType,
    field
  } = metricByWeekIdValues;

  // Use Redux display names - these are computed from the actual time period dates
  const primaryLegendDisplayName = useAppSelector((state) => state.mainTimePeriod.displayName);
  const comparisonLegendDisplayName = useAppSelector((state) => state.comparisonTimePeriod.displayName);

  const useLatestWeek = field && field.timePeriodAggregationFunctionType === 'lastValue';
  const mainMetric = useLatestWeek ? formattedLastPrimaryValue : formattedPrimaryTotal;
  const secondaryMetric = useLatestWeek ? formattedLastComparisonValue : formattedComparisonTotal;

  const rawChange = useLatestWeek ? lastValuePercentChange : percentChange;
  const change = useLatestWeek ? formattedLastValuePercentChange : formattedPercentChange;

  const titleToUse = title || field.displayName;

  const convertSeriesToDelimitedData = () => {
    try {
      const data = convertSplineChartV2SeriesToDelimitedData(
        primarySeries,
        comparisonSeries,
        primaryLegendDisplayName,
        comparisonLegendDisplayName
      );

      // Create a blob with the data and save it as a CSV file
      const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, `${titleToUse}.csv`);
    } catch (e) {
      console.error('error: ', e);
    }

    return Promise.resolve();
  };

  const getBeaconChartWithLegendProps = () => {
    const defaultProps: Omit<BeaconChartWithLegendProps, 'children'> = {
      title: titleToUse,
      primaryLegendProps: {
        displayName: primaryLegendDisplayName,
        metric: mainMetric,
        change,
        direction: getDirection(rawChange)
      },
      comparisonLegendProps: {
        displayName: comparisonLegendDisplayName,
        metric: secondaryMetric,
        direction: getDirection(rawChange)
      },
      loading: isLoading,
      loadingComponent: <SplineChartLoading {...splineChartLoadingProps} />,
      showTitleSummary,
      buildDropdownMenuOptions,
      dropdownOptionHandler,
      convertSeriesToDelimitedData,
      ...chartWithLegendProps
    };

    if (beaconChartWithLegendOverride) {
      return beaconChartWithLegendOverride(defaultProps, metricByWeekIdValues);
    }
    return defaultProps;
  };

  return (
    <BeaconChartWithLegend {...getBeaconChartWithLegendProps()}>
      <SplineChartV2
        primarySeries={primarySeries}
        comparisonSeries={comparisonSeries}
        metricType={metricType}
        NoDataPlaceholder={
          <NoDataPlaceHolder
            title="Data currently unavailable"
            description="There is currently no data matching your selected criteria. Please try another query to view insights."
            flexProps={{ margin: '80px auto' }}
          />
        }
        {...splineChartProps}
      />
    </BeaconChartWithLegend>
  );
};

export default AtlasV2MetricSpineChart;
